import { clamp } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { loadReactiveUser } from "@/backend/http/user";
import { windowFromRelative } from "@/math/coordinate-transform";
import { isOffScreen } from "@/math/coordinate-utils";
import { distance2, interpolate } from "@/math/coordinates";
import { pointerPosHide, pointerTrailDotDist, pointerTrailMaxLen } from "@/model/Settings";
import type { Pointer } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { RelativeCoordinate, WindowCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { TechnicalUser } from "@/model/user";
import { isEmpty } from "@/utils/record";
import { asReadonly, asSemiReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { useDrawStore } from "./draw";
import { toggleFunc } from "./helpers/toggle";
import { useUserStore } from "./user";

interface PointerEvent {
  id?: TechnicalUser["id"];
  boardId?: Board["id"];
  pos: RelativeCoordinate;
}

export const usePointerStore = defineStore("pointer", () => {
  const pointers = ref<Record<Pointer["user"]["id"], Pointer>>({});
  const active = ref(false);
  let cleanupInterval = 0;

  const isDisabled = computed(() => useDrawStore().active || !useUserStore().isAllowed("edit"));

  watch(isDisabled, (disabled) => {
    if (disabled && usePointerStore().active) {
      usePointerStore().toggle();
    }
  });

  function set(pointerEvent: PointerEvent) {
    ensureCleanupIsRunning();
    const currentBoardId = useBoardStore().currentBoard().id;
    const boardId = pointerEvent.boardId || currentBoardId;
    const id = pointerEvent.id || useUserStore().technicalUser.id;
    if (boardId !== currentBoardId) {
      return;
    }
    if (isOffScreen(pointerEvent.pos)) {
      delete pointers.value[id];
      return;
    }

    const pointer = getPointer(id);
    pointer.timestamp = Date.now();
    addPointerPos(pointer, pointerEvent.pos);
  }

  function ensureCleanupIsRunning() {
    if (!cleanupInterval) {
      cleanupInterval = window.setInterval(cleanupPointers, pointerPosHide);
    }
  }

  function cleanupPointers() {
    const now = Date.now();
    for (const id in pointers.value) {
      if (now - pointers.value[id].timestamp > pointerPosHide) {
        delete pointers.value[id];
      }
    }
    if (isEmpty(pointers.value)) {
      stopCleanup();
    }
  }

  function getPointer(id: TechnicalUser["id"]) {
    let pointer = pointers.value[id];
    if (!pointer) {
      pointer = {
        user: loadReactiveUser({ id, caller: "pointer" }),
        relativePos: relativeCoord(0, 0),
        windowPos: [],
        timestamp: 0,
      };
      pointers.value = { ...pointers.value, [id]: pointer };
    }
    return pointer;
  }

  function reset() {
    stopCleanup();
    pointers.value = {};
    active.value = false;
  }

  function stopCleanup() {
    clearInterval(cleanupInterval);
    cleanupInterval = 0;
  }

  return {
    pointers: asSemiReadonly(pointers),
    active: asReadonly(active),
    isDisabled,
    toggle: toggleFunc(active),
    set,
    reset,
  };
});

function addPointerPos(p: Pointer, relativePos: RelativeCoordinate) {
  p.relativePos = { ...relativePos };
  const windowPos = windowFromRelative(relativePos);
  if (p.windowPos.length === 0) {
    p.windowPos.unshift({ index: 0, pos: windowPos });
  } else {
    interpolatePointerPos(p, windowPos);
    if (p.windowPos.length > pointerTrailMaxLen) {
      p.windowPos.splice(pointerTrailMaxLen);
    }
  }
}

function interpolatePointerPos(p: Pointer, windowPos: WindowCoordinate) {
  const lastPos = p.windowPos[0];
  const dist = Math.sqrt(distance2(windowPos, lastPos.pos));
  const steps = clamp(Math.round(dist / pointerTrailDotDist), 1, pointerTrailMaxLen);
  const stepLen = 1 / steps;
  for (let i = 1; i <= steps; i++) {
    const pos = interpolate(i * stepLen, lastPos.pos, windowPos);
    p.windowPos.unshift({ index: lastPos.index + Math.round(100 * i * stepLen), pos });
  }
}
